import type { TooltipContent } from "../render/RenderContext.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import type { KeyEventArgs, MouseEventArgs, WheelEventArgs } from "../core/events.js";
import { rectContains, type Point, type Rect } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { WebwinImage } from "../../engine/ImageStore.js";
import type { RenderContext } from "../render/RenderContext.js";
import { drawCross, drawTriangle } from "../themes/glyphs.js";
import type { TabVisualState, Theme } from "../themes/Theme.js";
import { Panel } from "./Panel.js";

export type TabPlacement = "top" | "bottom";

export interface TabPageOptions {
  icon?: WebwinImage | null;
  enabled?: boolean;
  /** Show an × on the tab. */
  closable?: boolean;
  tooltip?: string;
  tag?: unknown;
}

/** One tab and the page it shows. */
export interface TabPage extends TabPageOptions {
  text: string;
  /** The container to put this page's controls in. */
  readonly panel: Panel;
}

export interface TabControlOptions extends ContainerOptions {
  placement?: TabPlacement;
  tabHeight?: number;
  font?: FontSpec;
  /** Give every tab a close button. Individual pages can still opt out. */
  closable?: boolean;
}

const TAB_PADDING = 12;
const TAB_GAP = 2;
const ICON_SIZE = 16;
const CLOSE_SIZE = 16;
const SCROLL_BUTTON = 18;
const ICON_GAP = 5;

/**
 * A strip of tabs over a stack of pages.
 *
 * Each page is a `Panel` the control owns and shows one at a time, so an
 * application fills `page.controls` and never manages visibility itself.
 *
 * Top and bottom placement only: side tabs need rotated text, and every
 * transform in this renderer is translate plus uniform scale.
 */
export class TabControl extends Container {
  #pages: TabPage[] = [];
  #selected = -1;
  #hover = -1;
  #hoverClose = -1;
  #scroll = 0;
  /**
   * True while a gesture that began on the strip is scrolling it, and on
   * through the throw the finger leaves behind.
   */
  #swiping = false;
  #font: FontSpec | null;

  placement: TabPlacement;
  tabHeight: number;
  closable: boolean;

  constructor(options: TabControlOptions = {}) {
    super({ width: 360, height: 240, tabStop: true, cursor: "default", ...options });
    this.placement = options.placement ?? "top";
    this.tabHeight = options.tabHeight ?? 28;
    this.closable = options.closable ?? false;
    this.#font = options.font ?? null;

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerleave", () => this.#setHover(-1, -1));
    this.on("wheel", (e) => this.#onWheel(e));
    this.on("keydown", (e) => this.#onKeyDown(e));
  }

  // --- pages ------------------------------------------------------------

  get pages(): readonly TabPage[] {
    return this.#pages;
  }

  get count(): number {
    return this.#pages.length;
  }

  /** Add a page and return the panel to fill. */
  addPage(text: string, options: TabPageOptions = {}): Panel {
    const panel = new Panel({ style: "none", visible: false });
    const page: TabPage = { text, panel, ...options };
    this.#pages.push(page);
    this.controls.add(panel);
    if (this.#selected < 0) this.selectedIndex = this.#pages.length - 1;
    this.performLayout();
    this.invalidate();
    return panel;
  }

  removePage(index: number): void {
    const page = this.#pages[index];
    if (!page) return;
    this.#pages.splice(index, 1);
    this.controls.remove(page.panel);
    // Keep something selected, preferring the tab that slid into this slot.
    if (this.#pages.length === 0) this.#selected = -1;
    else if (this.#selected >= this.#pages.length) this.#selected = this.#pages.length - 1;
    else if (this.#selected > index) this.#selected -= 1;
    this.#syncVisibility();
    this.performLayout();
    this.invalidate();
    this.emit("selectionchanged");
  }

  pageAt(index: number): TabPage | undefined {
    return this.#pages[index];
  }

  indexOfPanel(panel: Panel): number {
    return this.#pages.findIndex((p) => p.panel === panel);
  }

  get selectedIndex(): number {
    return this.#selected;
  }
  set selectedIndex(v: number) {
    const next = v < 0 || v >= this.#pages.length ? -1 : v;
    if (next === this.#selected) return;
    const page = this.#pages[next];
    if (page && page.enabled === false) return;
    this.#selected = next;
    this.#syncVisibility();
    this.#scrollIntoView(next);
    this.invalidate();
    this.emit("selectionchanged");
  }

  get selectedPage(): TabPage | undefined {
    return this.#pages[this.#selected];
  }

  /** The panel of the page currently showing, or null. */
  get selectedPanel(): Panel | null {
    return this.#pages[this.#selected]?.panel ?? null;
  }

  #syncVisibility(): void {
    // Hiding the outgoing page drops any focus inside it, which would leave
    // the window with nothing focused. Take it onto the strip instead, where
    // the arrow keys then work.
    const focus = this.desktop?.focusManager;
    const hadFocus = !!focus && this.contains(focus.focused);
    this.#pages.forEach((page, i) => {
      page.panel.visible = i === this.#selected;
    });
    if (hadFocus && !focus.focused) this.focus();
  }

  // --- geometry ---------------------------------------------------------

  private get atBottom(): boolean {
    return this.placement === "bottom";
  }

  /** The tab strip, in local coordinates. */
  get stripRect(): Rect {
    const h = Math.min(this.tabHeight, this.height);
    return { x: 0, y: this.atBottom ? this.height - h : 0, width: this.width, height: h };
  }

  /** The page area, in local coordinates. */
  get pageRect(): Rect {
    const h = Math.min(this.tabHeight, this.height);
    return { x: 0, y: this.atBottom ? 0 : h, width: this.width, height: Math.max(0, this.height - h) };
  }

  override get clientRect(): Rect {
    // Children are the pages, so the client area is the page area inset by
    // the border the control paints around it.
    const r = this.pageRect;
    return { x: r.x + 1, y: r.y + 1, width: Math.max(0, r.width - 2), height: Math.max(0, r.height - 2) };
  }

  isClosable(page: TabPage): boolean {
    return page.closable ?? this.closable;
  }

  /** Width of one tab, measured against the theme's font. */
  tabWidth(index: number, rc: RenderContext, theme: Theme): number {
    const page = this.#pages[index];
    if (!page) return 0;
    const font = this.#font ?? theme.metrics.font;
    let w = TAB_PADDING * 2 + Math.ceil(rc.measureText(page.text, font).width);
    if (page.icon) w += ICON_SIZE + ICON_GAP;
    if (this.isClosable(page)) w += CLOSE_SIZE + ICON_GAP;
    return Math.max(48, w);
  }

  totalTabWidth(rc: RenderContext, theme: Theme): number {
    let w = 0;
    for (let i = 0; i < this.#pages.length; i++) w += this.tabWidth(i, rc, theme) + TAB_GAP;
    return Math.max(0, w - TAB_GAP);
  }

  /** True when the strip cannot show every tab and needs its arrows. */
  overflowing(rc: RenderContext, theme: Theme): boolean {
    return this.totalTabWidth(rc, theme) > this.width;
  }

  /** Width the scroll arrows take off the right of the strip, 0 when hidden. */
  scrollBandWidth(rc: RenderContext, theme: Theme): number {
    return this.overflowing(rc, theme) ? SCROLL_BUTTON * 2 : 0;
  }

  /** Tab rectangle in local coordinates, scroll applied. */
  tabRect(index: number, rc: RenderContext, theme: Theme): Rect {
    const strip = this.stripRect;
    let x = 0;
    for (let i = 0; i < index; i++) x += this.tabWidth(i, rc, theme) + TAB_GAP;
    // The selected tab stands taller and reaches the page seam.
    const grow = index === this.#selected ? 0 : 2;
    return {
      x: x - this.#scroll,
      y: strip.y + (this.atBottom ? 0 : grow),
      width: this.tabWidth(index, rc, theme),
      height: strip.height - grow,
    };
  }

  /** The × box inside a tab, or null when the tab has no close button. */
  closeRect(index: number, rc: RenderContext, theme: Theme): Rect | null {
    const page = this.#pages[index];
    if (!page || !this.isClosable(page)) return null;
    const r = this.tabRect(index, rc, theme);
    return {
      x: r.x + r.width - TAB_PADDING - CLOSE_SIZE + 2,
      y: r.y + (r.height - CLOSE_SIZE) / 2,
      width: CLOSE_SIZE,
      height: CLOSE_SIZE,
    };
  }

  get scrollLeftRect(): Rect {
    const s = this.stripRect;
    return { x: this.width - SCROLL_BUTTON * 2, y: s.y, width: SCROLL_BUTTON, height: s.height };
  }

  get scrollRightRect(): Rect {
    const s = this.stripRect;
    return { x: this.width - SCROLL_BUTTON, y: s.y, width: SCROLL_BUTTON, height: s.height };
  }

  /** Tab index at a local point, or -1. */
  indexAt(p: Point, rc: RenderContext, theme: Theme): number {
    const strip = this.stripRect;
    if (p.y < strip.y || p.y > strip.y + strip.height) return -1;
    if (this.overflowing(rc, theme) && p.x >= this.width - this.scrollBandWidth(rc, theme)) return -1;
    for (let i = 0; i < this.#pages.length; i++) {
      if (rectContains(this.tabRect(i, rc, theme), p)) return i;
    }
    return -1;
  }

  override getTooltip(p: Point): TooltipContent | null {
    const d = this.desktop;
    if (!d) return this.tooltip;
    const i = this.indexAt(p, d.rc, d.theme);
    if (i < 0) return this.tooltip;
    return this.#pages[i]!.tooltip ?? this.#pages[i]!.text;
  }

  /** How far the strip is scrolled, in pixels. */
  get scrollOffset(): number {
    return this.#scroll;
  }

  /** Nudge the strip so tab `index` is fully visible. */
  #scrollIntoView(index: number): void {
    const d = this.desktop;
    if (!d || index < 0) return;
    this.scrollTabInView(index, d.rc, d.theme);
  }

  scrollTabInView(index: number, rc: RenderContext, theme: Theme): void {
    if (index < 0 || !this.overflowing(rc, theme)) return;
    const r = this.tabRect(index, rc, theme);
    const limit = this.width - this.scrollBandWidth(rc, theme);
    if (r.x < 0) this.#scroll += r.x;
    else if (r.x + r.width > limit) this.#scroll += r.x + r.width - limit;
    this.#clamp(rc, theme);
  }

  #clamp(rc: RenderContext, theme: Theme): void {
    const max = Math.max(0, this.totalTabWidth(rc, theme) - (this.width - this.scrollBandWidth(rc, theme)));
    this.#scroll = Math.max(0, Math.min(max, this.#scroll));
  }

  #clampScroll(): void {
    const d = this.desktop;
    if (d) this.#clamp(d.rc, d.theme);
    else if (this.#pages.length === 0) this.#scroll = 0;
  }

  scrollTabs(delta: number, rc?: RenderContext, theme?: Theme): void {
    this.#scroll += delta;
    if (rc && theme) this.#clamp(rc, theme);
    else this.#clampScroll();
    this.invalidate();
  }

  protected override performLayout(): void {
    if (!this.#pages) return;
    const r = this.clientRect;
    // Pages live in child space, which clientRect already offsets.
    for (const page of this.#pages) page.panel.setBounds(0, 0, r.width, r.height);
    this.#clampScroll();
  }

  // --- input ------------------------------------------------------------

  #setHover(tab: number, close: number): void {
    if (this.#hover === tab && this.#hoverClose === close) return;
    this.#hover = tab;
    this.#hoverClose = close;
    this.invalidate();
  }

  #onPointerMove(e: MouseEventArgs): void {
    const d = this.desktop;
    if (!d) return;
    const p = { x: e.x, y: e.y };
    const tab = this.indexAt(p, d.rc, d.theme);
    const closeBox = tab >= 0 ? this.closeRect(tab, d.rc, d.theme) : null;
    this.#setHover(tab, closeBox && rectContains(closeBox, p) ? tab : -1);
  }

  #onPointerDown(e: MouseEventArgs): void {
    const d = this.desktop;
    // A busy container swallows the pointer, and that includes its own strip.
    if (!d || this.inputBlocked) return;
    const p = { x: e.x, y: e.y };
    if (!rectContains(this.stripRect, p)) return;
    e.handled = true;
    this.focus();

    if (this.overflowing(d.rc, d.theme)) {
      if (rectContains(this.scrollLeftRect, p)) {
        this.scrollTabs(-60);
        return;
      }
      if (rectContains(this.scrollRightRect, p)) {
        this.scrollTabs(60);
        return;
      }
    }

    const index = this.indexAt(p, d.rc, d.theme);
    if (index < 0) return;
    const close = this.closeRect(index, d.rc, d.theme);
    if (close && rectContains(close, p)) {
      this.closePage(index);
      return;
    }
    this.selectedIndex = index;
  }

  /**
   * Close a tab, giving listeners a chance to stop it.
   *
   * `tabclosing` carries a cancel flag so an application can prompt to save
   * before the page disappears.
   */
  closePage(index: number): boolean {
    const page = this.#pages[index];
    if (!page) return false;
    const args = { index, cancel: false };
    this.emit("tabclosing", args);
    if (args.cancel) return false;
    this.removePage(index);
    this.emit("tabclosed", index);
    return true;
  }

  /** Move the selection by `delta`, skipping disabled tabs. */
  selectRelative(delta: number): void {
    const n = this.#pages.length;
    if (n === 0) return;
    let i = this.#selected;
    for (let step = 0; step < n; step++) {
      i = (((i + delta) % n) + n) % n;
      if (this.#pages[i]!.enabled !== false) {
        this.selectedIndex = i;
        return;
      }
    }
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (this.inputBlocked) return;
    // Ctrl+Tab cycles from anywhere inside; the arrows need the strip focused.
    if (e.key === "Tab" && e.ctrlKey) {
      this.selectRelative(e.shiftKey ? -1 : 1);
      e.handled = true;
      return;
    }
    if (!this.focused || e.ctrlKey || e.altKey || e.metaKey) return;
    switch (e.key) {
      case "ArrowRight":
        this.selectRelative(1);
        e.handled = true;
        return;
      case "ArrowLeft":
        this.selectRelative(-1);
        e.handled = true;
        return;
      case "Home":
        this.selectedIndex = 0;
        e.handled = true;
        return;
      case "End":
        this.selectedIndex = this.#pages.length - 1;
        e.handled = true;
        return;
      default:
        return;
    }
  }

  // --- swiping ----------------------------------------------------------

  /**
   * Dragging the strip sideways scrolls it, the way a phone scrolls a row of
   * tabs. The arrows stay for the mouse; a finger should not have to find an
   * 18px button.
   *
   * Only while the tabs overflow, and only for a gesture that began over the
   * strip: a drag across a page belongs to whatever the page holds, and
   * consuming nothing here is what lets it reach that instead. What is left
   * of a diagonal drag chains outward as usual, so a swipe that is mostly
   * downward still scrolls the pane this control sits in.
   */
  override panBy(dx: number, _dy: number): Point {
    const none = { x: 0, y: 0 };
    const d = this.desktop;
    if (dx === 0 || !d || this.inputBlocked) return none;
    if (!this.overflowing(d.rc, d.theme)) return none;
    if (!this.swiping && !this.#pointerOnStrip()) return none;
    this.#swiping = true;
    // The finger is pulling the strip along, not resting on any one tab.
    this.#setHover(-1, -1);
    const before = this.#scroll;
    this.scrollTabs(dx, d.rc, d.theme);
    return { x: this.#scroll - before, y: 0 };
  }

  /**
   * True while the strip is mid-gesture: the finger still down, or the throw
   * it left behind still running.
   *
   * A swipe that wanders off the strip on the way is still that swipe, so
   * where the gesture belongs is decided once, as it starts, and held until
   * the desktop reports it over.
   */
  get swiping(): boolean {
    const d = this.desktop;
    if (!this.#swiping) return false;
    if (d && (d.input.panning || d.flinging)) return true;
    this.#swiping = false;
    return false;
  }

  /**
   * A mouse drag over the strip swipes it too.
   *
   * On for the whole control although the pages sit inside it, because
   * `panBy` turns away anything that did not start on the strip — a drag over
   * a page still belongs to the page's own controls.
   */
  override get pansWithMouse(): boolean {
    return true;
  }

  #pointerOnStrip(): boolean {
    const d = this.desktop;
    return !!d && rectContains(this.stripRect, this.fromScreen(d.input.pointerPosition));
  }

  /**
   * The wheel over the strip scrolls it, which is the desktop half of the
   * swipe.
   *
   * A plain vertical wheel counts: the strip has only the one axis, and a
   * wheel over it that did nothing would be a dead patch in the window.
   * Leaving it unhandled lets it bubble, so a wheel over a page still reaches
   * whatever scrolls there.
   */
  #onWheel(e: WheelEventArgs): void {
    const d = this.desktop;
    if (!d || this.inputBlocked) return;
    if (!rectContains(this.stripRect, { x: e.x, y: e.y })) return;
    if (!this.overflowing(d.rc, d.theme)) return;
    const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
    if (delta === 0) return;
    const before = this.#scroll;
    this.scrollTabs(delta, d.rc, d.theme);
    if (this.#scroll !== before) e.handled = true;
  }

  // --- painting ---------------------------------------------------------

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    const page = this.pageRect;
    const font = this.#font ?? theme.metrics.font;

    // The page body first: tabs are drawn over its edge so the selected one
    // can merge into it.
    theme.drawPanel(rc, page, false);

    const strip = this.stripRect;
    const limit = this.width - this.scrollBandWidth(rc, theme);
    rc.clipped({ x: 0, y: strip.y, width: limit, height: strip.height }, () => {
      for (let i = 0; i < this.#pages.length; i++) {
        if (i === this.#selected) continue; // drawn last, over its neighbors
        this.#paintTab(rc, theme, i, font);
      }
      if (this.#selected >= 0) this.#paintTab(rc, theme, this.#selected, font);
    });

    if (this.overflowing(rc, theme)) this.#paintScrollButtons(rc, theme);
  }

  #paintTab(rc: RenderContext, theme: Theme, index: number, font: FontSpec): void {
    const page = this.#pages[index]!;
    const r = this.tabRect(index, rc, theme);
    if (r.x + r.width < 0 || r.x > this.width) return;

    const selected = index === this.#selected;
    const enabled = page.enabled !== false;
    const state: TabVisualState = {
      hovered: this.#hover === index && enabled,
      pressed: false,
      focused: selected && this.focused,
      disabled: !enabled,
      selected,
      bottom: this.atBottom,
    };
    theme.drawTab(rc, r, state);

    if (selected) {
      // Paint out the seam so the tab and the page read as one surface.
      const y = this.atBottom ? r.y : r.y + r.height - 1;
      rc.fillRect({ x: r.x + 1, y, width: r.width - 2, height: 1 }, theme.palette.windowBg);
    }

    const ink = theme.tabTextColor(state);
    let x = r.x + TAB_PADDING;
    if (page.icon) {
      rc.drawTexture(page.icon, {
        x,
        y: r.y + (r.height - ICON_SIZE) / 2,
        width: ICON_SIZE,
        height: ICON_SIZE,
      });
      x += ICON_SIZE + ICON_GAP;
    }
    const close = this.closeRect(index, rc, theme);
    const textRight = close ? close.x - ICON_GAP : r.x + r.width - TAB_PADDING;
    rc.drawText(page.text, x, r.y + r.height / 2, font, ink, {
      baseline: "middle",
      maxWidth: Math.max(0, textRight - x),
    });

    if (close) {
      if (this.#hoverClose === index) {
        rc.fillRect(close, theme.palette.controlBgPressed, 3);
      }
      drawCross(rc, close, ink, 8);
    }
    if (state.focused) theme.drawFocusRing(rc, { x: r.x + 2, y: r.y + 2, width: r.width - 4, height: r.height - 4 });
  }

  #paintScrollButtons(rc: RenderContext, theme: Theme): void {
    const palette = theme.palette;
    const max = Math.max(0, this.totalTabWidth(rc, theme) - (this.width - this.scrollBandWidth(rc, theme)));
    for (const [r, dir, off] of [
      [this.scrollLeftRect, "left", this.#scroll <= 0],
      [this.scrollRightRect, "right", this.#scroll >= max],
    ] as const) {
      rc.fillRect(r, palette.controlBg);
      rc.vLine(r.x, r.y + 2, r.height - 4, palette.gridLine);
      drawTriangle(rc, r, dir, off ? palette.textDisabled : palette.text, 4);
    }
  }
}
